// Pacientes duplicados (F1: detección y revisión, solo lectura).
// Centro Medico → Clínica → Pacientes duplicados (nivel 5). Pares candidatos por
// identificación, nombre normalizado o nombre parecido; comparación lado a lado
// con lo que contiene cada ficha y la ficha principal sugerida. La unificación
// (mover historia y movimientos a una sola ficha) llega en la fase 2.
import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { AlertTriangle, CheckCircle2, GitMerge, Loader2, RotateCcw, Save, Search, Star, Undo2, Users, XCircle } from 'lucide-react';
import { useSort, SortableTh } from '@/features/financiero/tablaSort';
import { Card, CardContent } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { getErrorMessage } from '@/lib/api/client';
import { medRpc } from './shared';
import { LOCALE } from '@/lib/utils/regional';

const fmtFecha = (s: string) => new Date(String(s).slice(0, 10) + 'T00:00:00').toLocaleDateString(LOCALE);

interface Ficha {
  id: string; codigo: string | null; nombre: string; identificacion: string | null; tipo_identificacion: string | null;
  id_provisional: boolean; fecha_nacimiento: string | null; fnac_ficticia: boolean; genero: string | null;
  email: string | null; telefono: string | null; aseguradora: string | null; alergias: string | null; creado: string;
  datos: Record<'evoluciones' | 'citas' | 'examenes' | 'recetas' | 'documentos' | 'consentimientos' | 'ventas' | 'compras' | 'portal', number>;
}
interface Par {
  a: string; b: string; criterio: 'identificacion' | 'nombre' | 'parecido'; confianza: 'alta' | 'revisar';
  similitud: number; descartado: boolean; motivo_descarte: string | null; sugerida: string; ficha_a: Ficha; ficha_b: Ficha;
}
interface Resp {
  resumen: { pacientes: number; alta: number; revisar: number; descartados: number; id_provisional: number; fnac_ficticia: number };
  pares: Par[];
}

const CRITERIO: Record<Par['criterio'], string> = {
  identificacion: 'Misma identificación',
  nombre: 'Mismo nombre',
  parecido: 'Nombre parecido',
};
const QK = ['med-duplicados'];
const sinTildes = (t: string) => t.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();

const CAMPOS: { k: keyof Ficha; t: string; fmt?: (f: Ficha) => string }[] = [
  { k: 'nombre', t: 'Nombre' },
  { k: 'identificacion', t: 'Identificación', fmt: (f) => `${f.identificacion ?? '—'}${f.tipo_identificacion ? ` (${f.tipo_identificacion})` : ''}` },
  { k: 'codigo', t: 'Código' },
  { k: 'fecha_nacimiento', t: 'Fecha de nacimiento', fmt: (f) => (f.fecha_nacimiento ? fmtFecha(f.fecha_nacimiento) : '—') },
  { k: 'genero', t: 'Género' },
  { k: 'telefono', t: 'Teléfono' },
  { k: 'email', t: 'Correo' },
  { k: 'aseguradora', t: 'Aseguradora' },
  { k: 'alergias', t: 'Alergias' },
  { k: 'creado', t: 'Creada el', fmt: (f) => fmtFecha(f.creado) },
];
const DATOS: { k: keyof Ficha['datos']; t: string }[] = [
  { k: 'evoluciones', t: 'Evoluciones' }, { k: 'citas', t: 'Citas' }, { k: 'examenes', t: 'Exámenes' },
  { k: 'recetas', t: 'Recetas' }, { k: 'documentos', t: 'Documentos' }, { k: 'consentimientos', t: 'Consentimientos' },
  { k: 'ventas', t: 'Ventas' }, { k: 'compras', t: 'Compras' }, { k: 'portal', t: 'Usuario de portal' },
];

const valor = (f: Ficha, c: (typeof CAMPOS)[number]) => (c.fmt ? c.fmt(f) : String(f[c.k] ?? '—') || '—');

export function PacientesDuplicados() {
  const qc = useQueryClient();
  const [vista, setVista] = useState<'alta' | 'revisar' | 'descartados' | 'unificadas' | 'corregir'>('alta');
  const [unificando, setUnificando] = useState<string | null>(null);
  const [q, setQ] = useState('');
  const [descartando, setDescartando] = useState<string | null>(null);
  const [motivo, setMotivo] = useState('');

  const { data, isLoading, isFetching } = useQuery<Resp>({
    queryKey: QK, queryFn: () => medRpc<Resp>('med_duplicados', { _incluir_descartados: true }),
  });
  const refrescar = () => void qc.invalidateQueries({ queryKey: QK });
  const descartar = useMutation({
    mutationFn: (p: Par) => medRpc('med_duplicado_descartar', { _a: p.a, _b: p.b, _motivo: motivo || null }),
    onSuccess: () => { toast.success('Marcado como «no son duplicados»'); setDescartando(null); setMotivo(''); refrescar(); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });
  const restaurar = useMutation({
    mutationFn: (p: Par) => medRpc('med_duplicado_restaurar', { _a: p.a, _b: p.b }),
    onSuccess: () => { toast.success('El par vuelve a la lista de revisión'); refrescar(); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  const pares = useMemo(() => {
    const words = sinTildes(q).split(/\s+/).filter(Boolean);
    return (data?.pares ?? []).filter((p) =>
      (vista === 'descartados' ? p.descartado : !p.descartado && p.confianza === vista)
      && (!words.length || words.every((w) => sinTildes(`${p.ficha_a.nombre} ${p.ficha_a.identificacion ?? ''} ${p.ficha_b.nombre} ${p.ficha_b.identificacion ?? ''}`).includes(w))));
  }, [data, vista, q]);
  const r = data?.resumen;

  return (
    <div className="space-y-4 p-4 sm:p-6">
      <div>
        <h1 className="flex items-center gap-2 text-xl font-semibold"><Users className="h-5 w-5" /> Pacientes duplicados</h1>
        <p className="text-sm text-muted-foreground">
          Fichas que parecen ser la misma persona. Compare los datos lado a lado; si no son la misma persona, márquelas como «no son duplicados».
          Los pares se pueden UNIFICAR en una sola ficha (los de «Revisar» piden confirmar que son la misma persona) y deshacer desde «Unificadas». En «Corregir datos provisionales» se completan en lote las identificaciones «PA…» y las fechas 01/01/2006.
        </p>
      </div>

      {r && (
        <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-6">
          {[
            [r.pacientes, 'Pacientes activos'], [r.alta, 'Duplicados (confianza alta)'], [r.revisar, 'Nombres parecidos (revisar)'],
            [r.descartados, 'Marcados «no son duplicados»'], [r.id_provisional, 'Con identificación provisional (PA…)'], [r.fnac_ficticia, 'Con fecha de nacimiento 01/01/2006'],
          ].map(([n, t]) => (
            <div key={String(t)} className="rounded-lg border p-3"><div className="text-xl font-semibold tabular-nums">{n}</div><div className="text-xs text-muted-foreground">{t}</div></div>
          ))}
        </div>
      )}

      <div className="flex flex-wrap items-center gap-2">
        <div className="inline-flex rounded-md border p-0.5">
          {([['alta', `Confianza alta (${r?.alta ?? 0})`], ['revisar', `Revisar (${r?.revisar ?? 0})`], ['descartados', `No son duplicados (${r?.descartados ?? 0})`],
             ['unificadas', 'Unificadas'], ['corregir', 'Corregir datos provisionales']] as const).map(([k, t]) => (
            <button key={k} type="button" onClick={() => setVista(k)}
              className={`rounded px-3 py-1.5 text-sm ${vista === k ? 'bg-primary text-primary-foreground' : 'hover:bg-muted'}`}>{t}</button>
          ))}
        </div>
        {['alta', 'revisar', 'descartados'].includes(vista) && <div className="relative">
          <Search className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Buscar por nombre o identificación…"
            className="h-9 w-72 max-w-full rounded-md border bg-background pl-8 pr-2 text-sm" />
        </div>}
        {isFetching && !isLoading && <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />}
      </div>

      {vista === 'unificadas' && <Unificadas />}
      {vista === 'corregir' && <CorregirLote />}
      {['alta', 'revisar', 'descartados'].includes(vista) && <>
      {isLoading && <p className="py-8 text-center text-muted-foreground"><Loader2 className="mr-1 inline h-4 w-4 animate-spin" /> Buscando duplicados…</p>}
      {!isLoading && pares.length === 0 && (
        <p className="py-8 text-center text-muted-foreground">
          {vista === 'descartados' ? 'No hay pares marcados como «no son duplicados».' : 'No hay pares en esta lista.'}
        </p>
      )}

      <div className="space-y-3">
        {pares.map((p, i) => {
          const clave = `${p.a}-${p.b}`;
          const fichas = [p.ficha_a, p.ficha_b];
          return (
            <Card key={clave}>
              <CardContent className="space-y-3 pt-4">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <div className="flex flex-wrap items-center gap-2 text-sm">
                    <span className="text-muted-foreground">#{i + 1}</span>
                    <span className={`rounded-full px-2 py-0.5 text-xs ${p.confianza === 'alta' ? 'bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-300' : 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300'}`}>
                      {CRITERIO[p.criterio]}{p.criterio === 'parecido' ? ` · ${Math.round(Number(p.similitud) * 100)} %` : ''}
                    </span>
                    {p.descartado && <span className="text-xs text-muted-foreground">No son duplicados{p.motivo_descarte ? `: ${p.motivo_descarte}` : ''}</span>}
                  </div>
                  <div className="flex items-center gap-2">
                    {p.descartado ? (
                      <Button size="sm" variant="outline" isLoading={restaurar.isPending && restaurar.variables === p} onClick={() => restaurar.mutate(p)}>
                        <RotateCcw className="mr-1 h-3.5 w-3.5" /> Volver a revisar</Button>
                    ) : descartando === clave || unificando === clave ? null : (
                      <>
                        <Button size="sm" onClick={() => { setUnificando(clave); setDescartando(null); }}>
                          <GitMerge className="mr-1 h-3.5 w-3.5" /> Unificar</Button>
                        <Button size="sm" variant="outline" onClick={() => { setDescartando(clave); setMotivo(''); setUnificando(null); }}>
                          <XCircle className="mr-1 h-3.5 w-3.5" /> No son duplicados</Button>
                      </>
                    )}
                  </div>
                </div>

                {unificando === clave && (
                  <UnificarPanel par={p} onClose={() => setUnificando(null)} onDone={() => { setUnificando(null); refrescar(); }} />
                )}
                {descartando === clave && (
                  <div className="flex flex-wrap items-center gap-2 rounded-md border bg-muted/30 p-2 text-sm">
                    <span>¿Por qué no son la misma persona?</span>
                    <input autoFocus value={motivo} onChange={(e) => setMotivo(e.target.value)} placeholder="Ej.: hermanos con el mismo nombre (opcional)"
                      className="h-8 min-w-64 flex-1 rounded border bg-background px-2 text-sm" />
                    <Button size="sm" isLoading={descartar.isPending} onClick={() => descartar.mutate(p)}>Confirmar</Button>
                    <Button size="sm" variant="ghost" onClick={() => setDescartando(null)}>Cancelar</Button>
                  </div>
                )}

                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b text-left text-xs text-muted-foreground">
                        <th className="w-44 py-1.5 pr-3">Dato</th>
                        {fichas.map((f, k) => (
                          <th key={f.id} className="py-1.5 pr-3">
                            <span className="inline-flex items-center gap-1">Ficha {k === 0 ? 'A' : 'B'}
                              {f.id === p.sugerida && <span className="inline-flex items-center gap-0.5 rounded-full bg-emerald-100 px-1.5 py-0.5 text-[10px] font-medium text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-300" title="Sugerida como principal: identificación y fecha reales, más historia clínica y movimientos">
                                <Star className="h-3 w-3" /> principal sugerida</span>}
                            </span>
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {CAMPOS.map((c) => {
                        const va = valor(fichas[0], c); const vb = valor(fichas[1], c);
                        const distinto = sinTildes(va).replace(/\s+/g, ' ') !== sinTildes(vb).replace(/\s+/g, ' ');
                        return (
                          <tr key={c.k} className="border-b last:border-0">
                            <td className="py-1 pr-3 text-xs text-muted-foreground">{c.t}</td>
                            {fichas.map((f, k) => (
                              <td key={f.id} className={`py-1 pr-3 ${distinto ? 'font-medium text-amber-700 dark:text-amber-400' : ''}`}>
                                {k === 0 ? va : vb}
                                {c.k === 'identificacion' && f.id_provisional && <span className="ml-1 text-[10px] text-muted-foreground">(provisional)</span>}
                                {c.k === 'fecha_nacimiento' && f.fnac_ficticia && <span className="ml-1 text-[10px] text-muted-foreground">(ficticia)</span>}
                              </td>
                            ))}
                          </tr>
                        );
                      })}
                      <tr className="border-t-2">
                        <td className="py-1.5 pr-3 text-xs font-medium">Contiene</td>
                        {fichas.map((f) => (
                          <td key={f.id} className="py-1.5 pr-3 text-xs">
                            {DATOS.filter((d) => Number(f.datos[d.k]) > 0).map((d) => `${d.t}: ${f.datos[d.k]}`).join(' · ') || <span className="text-muted-foreground">sin datos</span>}
                          </td>
                        ))}
                      </tr>
                    </tbody>
                  </table>
                </div>
                {fichas.every((f) => Object.values(f.datos).some((n) => Number(n) > 0)) && (
                  <p className="flex items-start gap-1.5 text-xs text-amber-700 dark:text-amber-400">
                    <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                    Las dos fichas tienen datos: al unificarlas se mueve todo a la principal sin perder nada.
                  </p>
                )}
              </CardContent>
            </Card>
          );
        })}
      </div>

      </>}
      {data && ['alta', 'revisar', 'descartados'].includes(vista) && (
        <p className="flex items-center gap-1.5 text-xs text-muted-foreground">
          <CheckCircle2 className="h-3.5 w-3.5" /> Los datos en ámbar son los que difieren entre las dos fichas. «Confianza alta»: misma identificación o mismo nombre
          (sin tildes, espacios ni orden de palabras). «Revisar»: nombres parecidos que pueden ser un error de tipeo o personas distintas; para unificarlos hay que confirmar que son la misma persona.
        </p>
      )}
    </div>
  );
}

// ── Unificar (panel en línea) ───────────────────────────────────────────────
const CAMPOS_UNIF: { k: string; t: string; v: (f: Ficha) => string; malo: (f: Ficha) => boolean }[] = [
  { k: 'nombre', t: 'Nombre', v: (f) => f.nombre ?? '', malo: (f) => !f.nombre },
  { k: 'identificacion', t: 'Identificación', v: (f) => `${f.identificacion ?? ''}${f.tipo_identificacion ? ` (${f.tipo_identificacion})` : ''}`, malo: (f) => f.id_provisional },
  { k: 'fecha_nacimiento', t: 'Fecha de nacimiento', v: (f) => (f.fecha_nacimiento ? fmtFecha(f.fecha_nacimiento) : ''), malo: (f) => !f.fecha_nacimiento || f.fnac_ficticia },
  { k: 'genero', t: 'Género', v: (f) => f.genero ?? '', malo: (f) => !f.genero },
  { k: 'telefono', t: 'Teléfono', v: (f) => f.telefono ?? '', malo: (f) => !f.telefono },
  { k: 'email', t: 'Correo', v: (f) => f.email ?? '', malo: (f) => !f.email },
  { k: 'aseguradora', t: 'Aseguradora', v: (f) => f.aseguradora ?? '', malo: (f) => !f.aseguradora },
  { k: 'alergias', t: 'Alergias', v: (f) => f.alergias ?? '', malo: (f) => !f.alergias },
];

function UnificarPanel({ par, onClose, onDone }: { par: Par; onClose: () => void; onDone: () => void }) {
  const [principalId, setPrincipalId] = useState(par.sugerida);
  const principal = principalId === par.a ? par.ficha_a : par.ficha_b;
  const duplicado = principalId === par.a ? par.ficha_b : par.ficha_a;
  const difieren = CAMPOS_UNIF.filter((c) => sinTildes(c.v(principal)).trim() !== sinTildes(c.v(duplicado)).trim());
  // por defecto se toma del duplicado lo que en la principal falta o es provisional/ficticio
  const porDefecto = () => Object.fromEntries(difieren.map((c) => [c.k, c.malo(principal) && !c.malo(duplicado)]));
  const [delDup, setDelDup] = useState<Record<string, boolean>>(porDefecto);
  // 980: un par de «Revisar» (nombre parecido) exige confirmar que es la misma persona
  const parecido = par.confianza === 'revisar';
  const [confirmo, setConfirmo] = useState(false);
  useEffect(() => { setDelDup(porDefecto()); }, [principalId]); // eslint-disable-line react-hooks/exhaustive-deps

  const unificar = useMutation({
    mutationFn: () => medRpc<{ filas_movidas: number; antecedentes_combinados: number }>('med_unificar_pacientes', {
      _principal: principal.id, _duplicado: duplicado.id, _del_duplicado: Object.entries(delDup).filter(([, v]) => v).map(([k]) => k),
      _confirmar_parecido: parecido && confirmo,
    }),
    onSuccess: (r) => { toast.success(`Fichas unificadas: ${r.filas_movidas} registro(s) movidos a la principal${r.antecedentes_combinados ? ` · ${r.antecedentes_combinados} antecedente(s) combinados` : ''}`); onDone(); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });
  const mueve = DATOS.filter((d) => Number(duplicado.datos[d.k]) > 0).map((d) => `${d.t}: ${duplicado.datos[d.k]}`).join(' · ');

  return (
    <div className="space-y-3 rounded-md border-2 border-primary/40 bg-primary/5 p-3 text-sm">
      <p className="font-medium">Unificar en una sola ficha</p>
      <div className="flex flex-wrap items-center gap-4">
        <span className="text-muted-foreground">Ficha que se CONSERVA:</span>
        {[par.ficha_a, par.ficha_b].map((f, k) => (
          <label key={f.id} className="flex items-center gap-1.5">
            <input type="radio" name={`pri-${par.a}`} checked={principalId === f.id} onChange={() => setPrincipalId(f.id)} />
            Ficha {k === 0 ? 'A' : 'B'} · {f.identificacion ?? 's/i'}{f.id === par.sugerida ? ' (sugerida)' : ''}
          </label>
        ))}
      </div>
      {difieren.length > 0 ? (
        <table className="w-full text-xs">
          <thead><tr className="border-b text-left text-muted-foreground"><th className="py-1 pr-2">Dato que difiere</th><th className="py-1 pr-2">Quedarse con el de la principal</th><th className="py-1">Tomar el del duplicado</th></tr></thead>
          <tbody>
            {difieren.map((c) => (
              <tr key={c.k} className="border-b last:border-0">
                <td className="py-1 pr-2 text-muted-foreground">{c.t}</td>
                <td className="py-1 pr-2"><label className="flex items-center gap-1.5"><input type="radio" checked={!delDup[c.k]} onChange={() => setDelDup({ ...delDup, [c.k]: false })} /> {c.v(principal) || '—'}</label></td>
                <td className="py-1"><label className="flex items-center gap-1.5"><input type="radio" checked={!!delDup[c.k]} onChange={() => setDelDup({ ...delDup, [c.k]: true })} /> {c.v(duplicado) || '—'}</label></td>
              </tr>
            ))}
          </tbody>
        </table>
      ) : <p className="text-xs text-muted-foreground">Los datos de ambas fichas coinciden.</p>}
      <p className="text-xs">
        <b>Se moverá a la ficha que se conserva:</b> {mueve || 'el duplicado no tiene historia ni movimientos'}. La ficha duplicada queda <b>inactiva</b> (no se borra) y la
        unificación se puede <b>deshacer</b> desde la pestaña «Unificadas». Los comprobantes ya emitidos al SRI no cambian.
      </p>
      {parecido && (
        <label className="flex items-start gap-2 rounded border border-amber-300 bg-amber-50 p-2 text-xs font-medium text-amber-900 dark:border-amber-800 dark:bg-amber-950/30 dark:text-amber-200">
          <input type="checkbox" className="mt-0.5" checked={confirmo} onChange={(e) => setConfirmo(e.target.checked)} />
          <span>Confirmo que son la MISMA persona (los nombres solo se parecen en un {Math.round(Number(par.similitud) * 100)} %). Revise identificación y fecha de nacimiento antes de unificar; quedará registrado en el historial.</span>
        </label>
      )}
      <div className="flex gap-2">
        <Button size="sm" isLoading={unificar.isPending} disabled={parecido && !confirmo} onClick={() => unificar.mutate()}><GitMerge className="mr-1 h-3.5 w-3.5" /> Unificar ahora</Button>
        <Button size="sm" variant="ghost" onClick={onClose}>Cancelar</Button>
      </div>
    </div>
  );
}

// ── Unificaciones realizadas (con deshacer) ─────────────────────────────────
interface Fusion {
  id: string; fecha: string; deshecha_at: string | null; principal: string; duplicado: string; duplicado_identificacion: string | null;
  del_duplicado: string[]; resumen: { filas_movidas?: number } | null; usuario: string | null; confirmado_parecido?: boolean;
}
function Unificadas() {
  const qc = useQueryClient();
  const [confirmar, setConfirmar] = useState<string | null>(null);
  const { data = [], isLoading } = useQuery<Fusion[]>({ queryKey: ['med-fusiones'], queryFn: () => medRpc<Fusion[]>('med_fusiones') });
  const filas = useMemo(() => data.map((f) => ({ ...f, filas: Number(f.resumen?.filas_movidas ?? 0), estado: f.deshecha_at ? 'Deshecha' : 'Vigente' })), [data]);
  const { sorted, sort, toggle } = useSort(filas);
  const deshacer = useMutation({
    mutationFn: (id: string) => medRpc<{ filas_devueltas: number }>('med_deshacer_fusion', { _fusion: id }),
    onSuccess: (r) => { toast.success(`Unificación deshecha: ${r.filas_devueltas} registro(s) devueltos a la ficha original`); setConfirmar(null); void qc.invalidateQueries({ queryKey: ['med-fusiones'] }); void qc.invalidateQueries({ queryKey: QK }); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });
  if (isLoading) return <p className="py-8 text-center text-muted-foreground"><Loader2 className="mr-1 inline h-4 w-4 animate-spin" /> Cargando…</p>;
  return (
    <Card><CardContent className="overflow-x-auto pt-4">
      <table className="w-full text-sm">
        <thead><tr className="border-b text-left text-xs text-muted-foreground">
          <th className="py-2 pr-3">N°</th>
          <SortableTh col="fecha" label="Fecha" sort={sort} onSort={toggle} />
          <SortableTh col="principal" label="Ficha que se conservó" sort={sort} onSort={toggle} />
          <SortableTh col="duplicado" label="Ficha unificada (inactiva)" sort={sort} onSort={toggle} />
          <th className="py-2 pr-3">Datos tomados del duplicado</th>
          <SortableTh col="filas" label="Registros movidos" align="right" sort={sort} onSort={toggle} />
          <SortableTh col="usuario" label="Usuario" sort={sort} onSort={toggle} />
          <SortableTh col="estado" label="Estado" sort={sort} onSort={toggle} />
          <th />
        </tr></thead>
        <tbody>
          {sorted.length === 0 && <tr><td colSpan={9} className="py-8 text-center text-muted-foreground">Todavía no se ha unificado ningún paciente.</td></tr>}
          {sorted.map((f, i) => (
            <tr key={f.id} className="border-b last:border-0">
              <td className="py-1.5 pr-3 text-muted-foreground">{i + 1}</td>
              <td className="py-1.5 pr-3 whitespace-nowrap">{new Date(f.fecha).toLocaleString(LOCALE)}</td>
              <td className="py-1.5 pr-3">{f.principal}</td>
              <td className="py-1.5 pr-3">{f.duplicado}<div className="text-xs text-muted-foreground">{f.duplicado_identificacion}{f.confirmado_parecido ? ' · nombre parecido (confirmado)' : ''}</div></td>
              <td className="py-1.5 pr-3 text-xs">{f.del_duplicado.length ? f.del_duplicado.join(', ') : '—'}</td>
              <td className="py-1.5 pr-3 text-right tabular-nums">{f.filas}</td>
              <td className="py-1.5 pr-3">{f.usuario ?? '—'}</td>
              <td className="py-1.5 pr-3"><span className={`rounded-full px-2 py-0.5 text-xs ${f.deshecha_at ? 'bg-muted text-muted-foreground' : 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-300'}`}>{f.estado}</span></td>
              <td className="py-1.5 text-right whitespace-nowrap">
                {!f.deshecha_at && (confirmar === f.id ? (
                  <span className="inline-flex items-center gap-1 text-xs">¿Deshacer?
                    <Button size="sm" variant="outline" isLoading={deshacer.isPending} onClick={() => deshacer.mutate(f.id)}>Sí, deshacer</Button>
                    <Button size="sm" variant="ghost" onClick={() => setConfirmar(null)}>No</Button>
                  </span>
                ) : (
                  <Button size="sm" variant="outline" onClick={() => setConfirmar(f.id)}><Undo2 className="mr-1 h-3.5 w-3.5" /> Deshacer</Button>
                ))}
              </td>
            </tr>
          ))}
        </tbody>
        {sorted.length > 0 && <tfoot><tr className="border-t-2 bg-muted/30 font-semibold">
          <td className="py-2 pl-0 pr-3" colSpan={5}>{sorted.length} unificación(es) · {sorted.filter((f) => !f.deshecha_at).length} vigente(s)</td>
          <td className="py-2 pr-3 text-right tabular-nums">{sorted.reduce((a, f) => a + f.filas, 0)}</td><td colSpan={3} />
        </tr></tfoot>}
      </table>
      <p className="mt-2 text-xs text-muted-foreground">Deshacer devuelve a la ficha original exactamente los registros que se movieron, restaura los datos copiados y reactiva la ficha. Lo que se haya registrado en la principal después de unificar se queda en la principal.</p>
    </CardContent></Card>
  );
}

// ── Corrección en lote de identificación / fecha provisionales ──────────────
interface Provisional {
  id: string; codigo: string | null; nombre: string; identificacion: string | null; tipo_identificacion: string | null;
  fecha_nacimiento: string | null; id_provisional: boolean; fnac_ficticia: boolean;
}
type Cambio = { identificacion?: string; tipo_identificacion?: string; fecha_nacimiento?: string };
function CorregirLote() {
  const qc = useQueryClient();
  const [soloId, setSoloId] = useState(false);
  const [soloFn, setSoloFn] = useState(false);
  const [q, setQ] = useState('');
  const [cambios, setCambios] = useState<Record<string, Cambio>>({});
  const [errores, setErrores] = useState<string[]>([]);
  const { data = [], isLoading } = useQuery<Provisional[]>({ queryKey: ['med-provisionales'], queryFn: () => medRpc<Provisional[]>('med_pacientes_provisionales') });
  const filas = useMemo(() => {
    const words = sinTildes(q).split(/\s+/).filter(Boolean);
    return data.filter((p) => (!soloId || p.id_provisional) && (!soloFn || p.fnac_ficticia)
      && (!words.length || words.every((w) => sinTildes(`${p.nombre} ${p.identificacion ?? ''} ${p.codigo ?? ''}`).includes(w))));
  }, [data, soloId, soloFn, q]);
  const { sorted, sort, toggle } = useSort(filas);
  const set = (id: string, c: Cambio) => setCambios((m) => ({ ...m, [id]: { ...m[id], ...c } }));
  const pendientes = Object.entries(cambios).filter(([, c]) => Object.values(c).some((v) => (v ?? '').trim() !== ''));
  const guardar = useMutation({
    mutationFn: () => medRpc<{ actualizados: number; errores: string[] }>('med_pacientes_corregir_lote', {
      _cambios: pendientes.map(([paciente_id, c]) => ({ paciente_id, ...c })),
    }),
    onSuccess: (r) => {
      setErrores(r.errores ?? []);
      if (r.actualizados) toast.success(`${r.actualizados} paciente(s) corregidos`);
      if (r.errores?.length) toast.error(`${r.errores.length} cambio(s) no se guardaron: revise la lista`);
      // se conservan solo los cambios que dieron error, para corregirlos
      setCambios((m) => Object.fromEntries(Object.entries(m).filter(([id]) => (r.errores ?? []).some((e) => e.includes(data.find((p) => p.id === id)?.nombre ?? '§')))));
      void qc.invalidateQueries({ queryKey: ['med-provisionales'] }); void qc.invalidateQueries({ queryKey: QK });
    },
    onError: (e) => toast.error(getErrorMessage(e)),
  });
  const inp = 'h-8 w-full min-w-0 rounded border bg-background px-2 text-sm';
  if (isLoading) return <p className="py-8 text-center text-muted-foreground"><Loader2 className="mr-1 inline h-4 w-4 animate-spin" /> Cargando…</p>;
  return (
    <Card><CardContent className="space-y-3 pt-4">
      <p className="text-sm text-muted-foreground">Fichas de la carga inicial con identificación provisional («PA…») o fecha de nacimiento 01/01/2006. Escriba la cédula o pasaporte real y la fecha correcta en las filas que quiera y pulse «Guardar». Las filas sin cambios no se tocan.</p>
      <div className="flex flex-wrap items-center gap-3">
        <div className="relative">
          <Search className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Buscar paciente…" className="h-9 w-64 rounded-md border bg-background pl-8 pr-2 text-sm" />
        </div>
        <label className="flex items-center gap-1.5 text-sm"><input type="checkbox" checked={soloId} onChange={(e) => setSoloId(e.target.checked)} /> Solo identificación provisional</label>
        <label className="flex items-center gap-1.5 text-sm"><input type="checkbox" checked={soloFn} onChange={(e) => setSoloFn(e.target.checked)} /> Solo fecha 01/01/2006</label>
        <Button size="sm" disabled={!pendientes.length} isLoading={guardar.isPending} onClick={() => guardar.mutate()}>
          <Save className="mr-1 h-3.5 w-3.5" /> Guardar {pendientes.length || ''} cambio(s)</Button>
        {pendientes.length > 0 && <Button size="sm" variant="ghost" onClick={() => setCambios({})}>Descartar cambios</Button>}
      </div>
      {errores.length > 0 && (
        <div className="rounded border border-red-300 bg-red-50 p-2 text-xs text-red-900 dark:border-red-800 dark:bg-red-950/30 dark:text-red-200">
          <p className="font-medium">No se guardaron:</p><ul className="list-disc pl-5">{errores.map((e) => <li key={e}>{e}</li>)}</ul>
        </div>
      )}
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead><tr className="border-b text-left text-xs text-muted-foreground">
            <th className="py-2 pr-3">N°</th>
            <SortableTh col="nombre" label="Paciente" sort={sort} onSort={toggle} />
            <SortableTh col="identificacion" label="Identificación actual" sort={sort} onSort={toggle} />
            <th className="py-2 pr-3">Nueva identificación</th>
            <th className="py-2 pr-3">Tipo</th>
            <SortableTh col="fecha_nacimiento" label="Fecha actual" sort={sort} onSort={toggle} />
            <th className="py-2 pr-3">Nueva fecha de nacimiento</th>
          </tr></thead>
          <tbody>
            {sorted.length === 0 && <tr><td colSpan={7} className="py-8 text-center text-muted-foreground">No hay fichas con datos provisionales.</td></tr>}
            {sorted.map((p, i) => {
              const c = cambios[p.id] ?? {};
              return (
                <tr key={p.id} className={`border-b last:border-0 ${Object.values(c).some((v) => (v ?? '').trim()) ? 'bg-amber-50/60 dark:bg-amber-950/20' : ''}`}>
                  <td className="py-1 pr-3 text-muted-foreground">{i + 1}</td>
                  <td className="py-1 pr-3">{p.nombre}<div className="text-xs text-muted-foreground">{p.codigo}</div></td>
                  <td className={`py-1 pr-3 ${p.id_provisional ? 'text-amber-700 dark:text-amber-400' : ''}`}>{p.identificacion ?? '—'}</td>
                  <td className="py-1 pr-3"><input className={`${inp} w-36`} value={c.identificacion ?? ''} onChange={(e) => set(p.id, { identificacion: e.target.value })} placeholder={p.id_provisional ? 'Cédula / pasaporte' : ''} aria-label={`Nueva identificación de ${p.nombre}`} /></td>
                  <td className="py-1 pr-3">
                    <select className={`${inp} w-32`} value={c.tipo_identificacion ?? ''} onChange={(e) => set(p.id, { tipo_identificacion: e.target.value })} aria-label={`Tipo de identificación de ${p.nombre}`}>
                      <option value="">{p.tipo_identificacion ?? '—'}</option><option value="cedula">cédula</option><option value="pasaporte">pasaporte</option><option value="ruc">ruc</option>
                    </select>
                  </td>
                  <td className={`py-1 pr-3 whitespace-nowrap ${p.fnac_ficticia ? 'text-amber-700 dark:text-amber-400' : ''}`}>{p.fecha_nacimiento ? fmtFecha(p.fecha_nacimiento) : '—'}</td>
                  <td className="py-1 pr-3"><input type="date" className={`${inp} w-40`} value={c.fecha_nacimiento ?? ''} onChange={(e) => set(p.id, { fecha_nacimiento: e.target.value })} aria-label={`Nueva fecha de nacimiento de ${p.nombre}`} /></td>
                </tr>
              );
            })}
          </tbody>
          {sorted.length > 0 && <tfoot><tr className="border-t-2 bg-muted/30 font-semibold">
            <td className="py-2 pr-3" colSpan={7}>{sorted.length} ficha(s) · {sorted.filter((p) => p.id_provisional).length} con identificación provisional · {sorted.filter((p) => p.fnac_ficticia).length} con fecha 01/01/2006 · {pendientes.length} cambio(s) sin guardar</td>
          </tr></tfoot>}
        </table>
      </div>
    </CardContent></Card>
  );
}
